// Subtle floating health bars for the heavy units (tanks + giants). Flat SQUARE-edged quads (a
// PlaneGeometry, no rounded corners), billboarded to face the camera, drawn on top so they read at
// a glance. A frame fills them via add() (reset() first, render() last). Rendered as TWO InstancedMesh
// (one bg, one fg) so ~46 live bars are 2 draw calls, not ~92 separate transparent draws.

import * as THREE from "three";

const CAP = 96;
const W = 32; // bar width (studs)
const H = 4; // bar height (studs) — thin + subtle

export class HealthBars {
  private group = new THREE.Group();
  private bg: THREE.InstancedMesh;
  private fg: THREE.InstancedMesh;
  private n = 0;
  // queued bar state, filled by add(), consumed (billboarded) by render()
  private px = new Float32Array(CAP);
  private py = new Float32Array(CAP);
  private pz = new Float32Array(CAP);
  private fillA = new Float32Array(CAP);
  private _m = new THREE.Matrix4();
  private _local = new THREE.Matrix4();
  private _p = new THREE.Vector3();
  private _q = new THREE.Quaternion();
  private _s = new THREE.Vector3(1, 1, 1);
  private _c = new THREE.Color();

  constructor(scene: THREE.Scene) {
    this.group.name = "healthbars";
    scene.add(this.group);

    const bgGeo = new THREE.PlaneGeometry(W, H);
    const fgGeo = new THREE.PlaneGeometry(W, H);
    fgGeo.translate(W / 2, 0, 0); // pivot at the LEFT edge (local x=0) so scale.x grows the fill from the left
    const bgMat = new THREE.MeshBasicMaterial({ color: 0x0c0c0c, transparent: true, opacity: 0.55, depthTest: false, depthWrite: false });
    const fgMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, depthTest: false, depthWrite: false });

    this.bg = new THREE.InstancedMesh(bgGeo, bgMat, CAP);
    this.fg = new THREE.InstancedMesh(fgGeo, fgMat, CAP);
    for (const m of [this.bg, this.fg]) {
      m.frustumCulled = false; // scattered bars; a global bound would mis-cull them
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      m.count = 0;
    }
    this.bg.renderOrder = 998;
    this.fg.renderOrder = 999; // over the bg (depthTest off, so render order decides)
    for (let i = 0; i < CAP; i++) this.fg.setColorAt(i, this._c.setHex(0x44dd44)); // allocate instanceColor
    this.group.add(this.bg, this.fg);
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  /** Start a frame — call before the units push their bars. */
  reset(): void {
    this.n = 0;
  }

  /** Queue a bar at world (x,y,z) with fill fraction `frac` (0..1). */
  add(x: number, y: number, z: number, frac: number): void {
    if (this.n >= CAP) return;
    const i = this.n++;
    this.px[i] = x; this.py[i] = y; this.pz[i] = z;
    this.fillA[i] = Math.max(0, Math.min(1, frac));
  }

  /** Finish the frame — billboard the used bars to the camera into the two instanced meshes. */
  render(camera: THREE.Camera): void {
    this._q.copy(camera.quaternion);
    for (let i = 0; i < this.n; i++) {
      this._p.set(this.px[i], this.py[i], this.pz[i]);
      this._m.compose(this._p, this._q, this._s); // bg: T(pos)·R(cameraFacing), unit scale
      this.bg.setMatrixAt(i, this._m);
      // fg: same billboard, then a local pivot-at-left scale so the bar shrinks from the left edge
      this._local.makeScale(Math.max(1e-4, this.fillA[i]), 1, 1);
      this._local.setPosition(-W / 2, 0, 0.3); // align left edge with bg + nudge in front
      this._m.multiply(this._local);
      this.fg.setMatrixAt(i, this._m);
      this.fg.setColorAt(i, this._c.setHSL(this.fillA[i] * 0.33, 0.85, 0.5)); // green (full) → red (low)
    }
    this.bg.count = this.n;
    this.fg.count = this.n;
    // no bars on screen (the common case out of combat) → skip two binds + draw calls per pass, ×4 passes
    this.bg.visible = this.n > 0;
    this.fg.visible = this.n > 0;
    if (this.n > 0) {
      this.bg.instanceMatrix.needsUpdate = true;
      this.fg.instanceMatrix.needsUpdate = true;
      if (this.fg.instanceColor) this.fg.instanceColor.needsUpdate = true;
    }
  }
}
